import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../ConnectStrip';
import { Head } from '../Head';
import { Hero } from '../Hero';
import { IndustriesServed } from '../IndustriesServed';
import { Summary } from '../Summary';

import * as css from './UIDesign.m.css';
import * as developmentCss from './Development.m.css';
import * as commonCss from '../../Common.m.css';
import FAQ from '../FAQ';

const designImg = require('../../assets/images/services/design.svg');
const designToolsImg = require('../../assets/images/services/design-tools.svg');
const sitepenLogo = require('../../assets/images/logos/sitepen.svg');
const uiDesignImg = require('../../assets/images/services/ui-design.svg');
const eyeImg = require('../../assets/images/services/eye.svg');
const softwareConsultingDesign = require('../../assets/images/development/software-consulting-design.svg');
const frontEndToolsImg = require('../../assets/images/development/front-end-tools.svg');
const consultingImg = require('../../assets/images/services/consulting.svg');
const dependImg = require('../../assets/images/home/depend.jpg');

const factory = create();

export const UIDesign = factory(function UIDesign() {
	return (
		<div classes={css.root}>
			<Head
				title="Enterprise Application UI Design | SitePen"
				description="SitePen specializes in enterprise UI design as part of our software development process to create the best possible products for our customers."
			/>
			<div classes={css.heroWrapper}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							xlTitle: 'UI Design'
						}}
					</Hero>
				</div>
			</div>
			<section classes={[commonCss.contentWrapper, commonCss.largePaddingTop]}>
				<Summary smaller reverse>
					{{
						title: 'Create the Best User Interactions',
						blurb: (
							<virtual>
								<p>
									User interface design (sometimes referred to as user interaction
									design) is the art of creating a visual design language for your
									product. Whether it’s navigation, buttons, form elements, or the
									typography throughout your app, each of these things should work
									together to create a consistent user experience that is clear
									and easy to understand. Products that have a strong UI design
									feel familiar to the customer and help them utilize each feature
									with minimal friction. Much like learning any language, there is
									a visual “vocabulary” associated with your product. Making that
									vocabulary consistent and clear is paramount to a clean user
									experience.
								</p>
								<p>
									Alongside a clean user experience, UI design provides a visual
									aesthetic that allows your users to identify your products,
									creating greater brand loyalty and awareness. Your enterprise
									app is more than just a logo in the navigation bar. It’s how all
									of the elements look and feel and work together. This is even
									more important for platforms with multiple products. A strong UI
									design can be incorporated into multiple products or apps,
									making it easy for users to switch from one to another.
								</p>
							</virtual>
						),
						image: <img src={designImg} classes={commonCss.responsiveImage} alt="" />
					}}
				</Summary>
				<Summary smaller>
					{{
						title: 'Enterprise Application UI Design',
						blurb: (
							<virtual>
								<p>
									Strong user interface design is even more important to the
									enterprise space. Enterprise software incorporates a greater
									amount of complexity, larger user base, and more critical
									features than most consumer applications. Because of this, it’s
									even more important that your UI design is clean and easy to
									understand. Colors, typography, buttons, navigation, and other
									elements should be well articulated and easy to consume by the
									eye. They should feel consistent between different screens and
									features, making the user feel at ease as they navigate through
									the entire experience.
								</p>
								<p>
									Another important aspect of enterprise UI design is that
									enterprise software often incorporates an entire suite of
									products or platforms. If each of your apps are speaking a
									different visual language, it will create confusion for the
									user, make each of them more difficult to navigate, and stunt
									any ability to express your brand consistently to your
									customers.
								</p>
							</virtual>
						),
						image: (
							<img
								src={dependImg}
								classes={commonCss.responsiveImageVertical}
								alt=""
								loading="lazy"
							/>
						)
					}}
				</Summary>
			</section>
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.contentWrapper}>
					<Summary smaller reverse margin={false}>
						{{
							title: 'Why Develop with SitePen',
							blurb: (
								<virtual>
									<p>
										At SitePen, we believe that quality is the single most
										important aspect of any project we undertake. Our commitment
										to delivering beautiful and modern user interface designs is
										a critical aspect to our approach to software design.
									</p>
									<p>
										You will start to experience this as soon as you reach out
										to us. Our Sales and Project Management teams work with you
										to understand your goals and expectations, identifying the
										characteristics that have the most significant influence on
										the application's design, and the visual direction that
										you’d like to take your product.
									</p>
									<p>
										Our design team will then develop the solution with these
										goals in mind. Using industry standard tools, they will
										create a beautiful and modern design language that elevates
										your brand and excites your customers. Each element will be
										designed with intent to be extensible to the entire user
										experience and your entire product suite. Whether working
										directly with your team or SitePen’s own engineers, they
										will prepare production-ready assets and work with
										developers to see the work through to implementation.
									</p>
								</virtual>
							),
							image: (
								<img
									src={sitepenLogo}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.section]}>
				<div classes={commonCss.contentWrapper}>
					<h2>UI Design Services</h2>
					<p>
						User interface design is a multi-faceted discipline that involves the
						smallest of pixel-perfect details while also being focused on big strategic
						thinking. It’s not simply creating a button or a card for the UI, it’s
						designing a flexible component that can be shared, reused, and adapted to
						multiple features of your enterprise application.
					</p>
					<ul classes={developmentCss.twoColumns}>
						<li classes={developmentCss.column}>
							<img
								classes={developmentCss.columnImage}
								src={uiDesignImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={developmentCss.columnTitle}>
								Interface Design and Optimization
							</h3>
							<p>
								To your customers, it may just be a form on the screen. They may
								just click buttons in the navigation bar without consciously
								considering the visual selection of colors, typography, and style.
								They might not think about the padding and the margins that make the
								content easy to consume and navigate. But these are the things our
								design team obsesses over. Whether you have an existing UI design
								that needs to be updated or need something built from the ground up,
								we specialize in hand crafting beautiful and modern user interface
								components that are natural extensions of the user experience. These
								UI elements make your products easier to use and also extend the
								look and feel of your brand to your end user.
							</p>
						</li>
						<li classes={developmentCss.column}>
							<img
								classes={developmentCss.columnImage}
								src={eyeImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={developmentCss.columnTitle}>
								Designing for Accessibility and WCAG Compliance
							</h3>
							<p>
								It is ever more important that the products we build are accessible
								to those with disabilities and other limitations. Software is moving
								toward the same regulation as other media, which require it to be
								usable to everyone regardless of any physical limitations. At
								SitePen we champion efforts to make the web and software
								applications open and accessible to everyone. This includes our
								approach to visual design and user interfaces. We design components
								and elements that are in line with current WCAG compliance
								standards. We consider color contrast, focus states, and typography
								that allow the app to be used by anyone with visual impairments or
								who cannot rely on a mouse for navigation.
							</p>
						</li>
						<li classes={developmentCss.column}>
							<img
								classes={developmentCss.columnImage}
								src={frontEndToolsImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={developmentCss.columnTitle}>Design Systems</h3>
							<p>
								A critical foundation to an extensible UI is a design system. A
								design system is a library of components and elements that can be
								reused and repurposed throughout the entire application. Rather than
								creating styles and elements for each view, a design system gives
								pieces of UI that can be created once and reused everywhere. This is
								a huge win for both designers and developers. Your design team will
								be able to focus on the important aspects of the user experience and
								how to bring critical features to your customers. Your development
								team will be able to work faster, building code and components that
								are reusable and reducing the amount of waste and duplicated effort.
							</p>
						</li>
						<li classes={developmentCss.column}>
							<img
								classes={developmentCss.columnImage}
								src={softwareConsultingDesign}
								alt=""
								loading="lazy"
							/>
							<h3 classes={developmentCss.columnTitle}>
								Style Guides and Design Specifications
							</h3>
							<p>
								A style guide goes hand in hand with a design system. It takes all
								of those well crafted UI elements and documents them in a way that
								your entire team or organization can reference. This gives everyone
								a single source of truth to build from, rather than each team
								designing and building in a silo. Using a style guide, developers
								can reference various design specifications to ensure what they
								build is consistent in look and feel with other parts of the
								application or with other products across your organization. This
								will enhance the user experience across your entire product
								offering, creating one easy to understand visual language.
							</p>
						</li>
					</ul>
				</div>
			</section>
			<section classes={commonCss.shaded}>
				<IndustriesServed />
			</section>
			<section classes={[commonCss.section, commonCss.contentWrapper]}>
				<Summary smaller reverse margin={false}>
					{{
						title: 'Technology We Use',
						blurb: (
							<p>
								SitePen utilizes the best leading-edge technology for our design
								process. We have in-depth experience with industry standard
								applications such as Sketch, Figma, and the Adobe Suite. Our
								abilities are not limited to mockups and static screenshots, but
								also incorporate clickable prototypes built with InVision, Framer,
								Axure, and Figma. We leverage existing design libraries and design
								systems such as Google’s Material Design and Bootstrap, and we are
								experts at developing custom systems ourselves. Whatever the need,
								we have experience with the right tools and technology to deliver
								fast and beautiful results for your project.
							</p>
						),
						image: (
							<img
								src={designToolsImg}
								classes={commonCss.responsiveImage}
								alt=""
								loading="lazy"
							/>
						)
					}}
				</Summary>
			</section>
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.smallContentWrapper}>
					<img
						src={consultingImg}
						classes={commonCss.centeredHeaderImage}
						alt=""
						loading="lazy"
					/>
					<h2>Choosing the Right UI Design Company</h2>
					<p>
						Choosing your design partner can be a critical part of your project’s
						success. Too many teams lack the experience to know what to look for in a UI
						design company. It’s important that you find someone who not only has a
						great portfolio, but also demonstrates that they can solve hard visual
						problems. A strong design team will challenge your ideas and show you new
						ways to approach your product.
					</p>
					<p>
						The designers at SitePen have comprehensive experience solving tough
						challenges. We’ve worked closely with enterprise companies to build tools
						for their internal teams as well as their external customers that are
						engaging, intuitive, and easy to use. We bring experience, speed, and
						agility to the design process.
					</p>
				</div>
			</section>
			<ConnectStrip />
			<FAQ title="Enterprise UI Design FAQs">
				{[
					{
						title: 'Why Is UI Design Important?',
						content: (
							<p>
								User Interface design is an important piece of any successful
								software app. It gives a face to your product that your customers
								can identify with, and it also provides important visual cues for
								how they can interact with the application. Having a consistent,
								clean UI design will not only make the customer experience fast and
								easy, but it will also reduce development time because pieces of the
								UI will be flexible and reusable across the app.
							</p>
						)
					},
					{
						title: 'How Long Does the Process Take?',
						content: (
							<p>
								UI design can be a matter of styling a few elements or pages, or it
								can be a robust engagement of defining an entire component library
								for your application. Whatever level of need your organization may
								have, our UI designers are fast at creating solutions that will
								bring the biggest impact to your product and customer base. Whether
								it’s a single sprint of effort or you’re looking for an ongoing
								design partner, SitePen has the experience to help build a beautiful
								and modern UI.
							</p>
						)
					},
					{
						title: 'Should My Company Think About UI Design?',
						content: (
							<p>
								Every software application needs a strong UI foundation. If this
								isn’t something that your company has, then it’s time to get some
								help. A clean UI will improve your customer’s experience, improve
								engagement, and help to create brand loyalty. It will also increase
								your development turnaround time, as elements in the product will be
								clearly defined and reusable across the entire experience.
							</p>
						)
					}
				]}
			</FAQ>
		</div>
	);
});

export default UIDesign;
